var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { deserialize, serialize } from "../serializers/symbolSerializer";
import { DynamicRender } from "../renders/Render";
import { MouseEventFlags, mouseEvents } from "../EventHandler";
import { listenDomEvent } from "../utils/domEvent";
/**
 * Symbol that renders a feature to the screen coordinate system. All symbols take as input a feature, target resolution
 * and target crs, and must return a set of renders (rendered primitives) that then can be used to draw the feature.
 * @alias sGis.Symbol
 */
export class Symbol {
    /**
     * This async function will be called every time the feature has to be drawn. It returns an array of renders that will actually be displayed on the map.
     * If the symbol cannot render provided feature, empty array is returned.
     * @param feature - feature to be drawn.
     * @param resolution - resolution of the render.
     * @param crs - target coordinate system of the render.
     */
    renderFunctionAsync(feature, resolution, crs) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.renderFunction(feature, resolution, crs);
        });
    }
    /**
     * Returns a copy of the symbol. Only essential properties are copied.
     */
    clone() {
        let desc = serialize(this);
        return deserialize(desc);
    }
}
export class DynamicPointSymbol extends Symbol {
    constructor({ offset = [0, 0], onRender } = {}) {
        super();
        this.offset = offset;
        this.onRender = onRender;
    }
    renderFunction(feature, resolution, crs) {
        let dynamicFeature = feature;
        if (dynamicFeature.__dynamicSymbolRender)
            return [dynamicFeature.__dynamicSymbolRender];
        let node = this._getFeatureNode(feature);
        node.style.position = 'absolute';
        dynamicFeature.__dynamicSymbolRender = new DynamicRender({
            node: node,
            onRender: this.onRender,
            update: (bbox = null, resolution = null) => {
                if (bbox === null)
                    bbox = dynamicFeature.__lastBbox;
                if (resolution === null)
                    resolution = dynamicFeature.__lastResolution;
                if (!bbox || !resolution || !dynamicFeature.crs.canProjectTo(bbox.crs))
                    return;
                let point = dynamicFeature.projectTo(bbox.crs);
                let dx = Math.round((point.x - bbox.xMin) / resolution + this.offset[0]);
                let dy = Math.round((bbox.yMax - point.y) / resolution + this.offset[1]);
                node.style.left = `${dx.toString()}px`;
                node.style.top = `${dy.toString()}px`;
                dynamicFeature.__lastBbox = bbox;
                dynamicFeature.__lastResolution = resolution;
            },
            redraw: () => {
                this._updateFeatureNode(dynamicFeature);
            }
        });
        this._setEventListeners(dynamicFeature);
        return [dynamicFeature.__dynamicSymbolRender];
    }
    getNode(feature) {
        let [render] = this.renderFunction(feature, 1, null);
        return render.node;
    }
    _setEventListeners(dynamicFeature) {
        if (dynamicFeature.eventFlags === MouseEventFlags.None)
            return;
        Object.keys(mouseEvents).forEach(eventName => {
            if (dynamicFeature.eventFlags & mouseEvents[eventName].flag) {
                listenDomEvent(dynamicFeature.__dynamicSymbolRender.node, mouseEvents[eventName].type, (event) => {
                    dynamicFeature.fire(mouseEvents[eventName].type, {
                        node: dynamicFeature.__dynamicSymbolRender.node,
                        browserEvent: event
                    });
                });
            }
        });
    }
    _updateFeatureNode(feature) {
        // do nothing
    }
    renderFunctionAsync(feature, resolution, crs) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.renderFunction(feature, resolution, crs);
        });
    }
}
